import { LoadingState } from "@repo/app-types/client/client";
import type { SlackIntegrationSettings } from "@repo/app-types/entities/integration";
import type { Project, ProjectUpdate } from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { SlackChannel } from "@repo/app-types/integrations/integrations";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectSlackIntegration } from "../integrations/integrationsSlice.js";
import { fetchSlackChannelsForIntegration } from "../settingsPage/recruitingIntegrationSettings/slackIntegrationSection.js";

// Cache the Slack channels to avoid fetching them multiple times.
let slackChannels: SlackChannel[] = [];

interface SlackProjectNotificationSectionProps {
  project: Project;
  onSave: (changes: ProjectUpdate) => void;
}

export const SlackProjectNotificationsSection: FC<
  SlackProjectNotificationSectionProps
> = ({ project, onSave }) => {
  const slackIntegration = useAppSelector(selectSlackIntegration);
  const navigate = useNavigate();

  const [slackChannelsLoadingState, setSlackChannelsLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);

  // Edits are held here until saved, like the email settings above this
  // section. Saving on every change made it easy to wipe the channel by
  // clearing the picker.
  const [isSlackEnabled, setIsSlackEnabled] = useState(
    project.settings.is_slack_enabled,
  );
  const [slackChannelId, setSlackChannelId] = useState(
    project.settings.slack_channel_id,
  );

  const hasChanges =
    isSlackEnabled !== project.settings.is_slack_enabled ||
    slackChannelId !== project.settings.slack_channel_id;

  useEffect(() => {
    const fetchSlackChannels = async () => {
      if (slackIntegration && slackChannels.length === 0) {
        try {
          setSlackChannelsLoadingState(LoadingState.LOADING);
          const channels = await fetchSlackChannelsForIntegration(
            slackIntegration.id,
          );

          slackChannels = channels;
          setSlackChannelsLoadingState(LoadingState.LOADED);
        } catch (err) {
          setSlackChannelsLoadingState(LoadingState.ERROR);
          console.log("Error fetching Slack channels", err);
        }
      }
    };

    fetchSlackChannels();
  }, [slackIntegration]);

  const renderContent = () => {
    if (!slackIntegration) {
      return (
        <div>
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  navigate(appPaths.settingsIntegrations.build());
                }}
                isDisabled={!allowed}
              >
                Enable Slack integration
              </Button>
            )}
          </Can>
        </div>
      );
    }

    const maybeRenderSelect = () => {
      if (!isSlackEnabled) return null;

      if (slackChannelsLoadingState === LoadingState.LOADING) {
        return <DefaultSkeleton />;
      }

      if (slackChannelsLoadingState === LoadingState.ERROR) {
        return (
          <div className="text-sm text-foreground">
            An unexpected error occurred loading Slack channels
          </div>
        );
      }

      if (!slackChannels.length) return null;

      const options = [...slackChannels];

      const companyLevelSlackChannelId = (
        slackIntegration.settings as SlackIntegrationSettings
      ).channel_id;

      const formattedCompanyWideChannelIndex = options.findIndex(
        (c) => c.id === companyLevelSlackChannelId,
      );

      const companyWideChannel = options[formattedCompanyWideChannelIndex];
      if (formattedCompanyWideChannelIndex !== -1 && companyWideChannel) {
        // If the company-wide channel can be found, format it with (Default) and place it at the start of the options array
        const formattedCompanyWideChannel = {
          ...companyWideChannel,
          name: `${companyWideChannel.name} (Default)`,
        };

        // Remove the original channel from the array
        options.splice(formattedCompanyWideChannelIndex, 1);
        // Prepend the formatted channel to the start of the array
        options.unshift(formattedCompanyWideChannel);
      }

      return (
        <div>
          <div className="mt-2 w-72">
            <Can
              I={PermissionActions.UPDATE}
              a={PermissionSubjects.JOB_OPENINGS}
              passThrough={true}
            >
              {(allowed) => (
                <ComboboxField<SlackChannel>
                  label="Target Slack channel"
                  items={options}
                  getLabel={(channel) => channel.name}
                  value={slackChannelId ?? companyLevelSlackChannelId ?? null}
                  onChange={(key) => {
                    setSlackChannelId(key === null ? null : String(key));
                  }}
                  placeholder="Search for a public channel..."
                  isDisabled={!allowed}
                />
              )}
            </Can>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            An interview summary and link to the full transcript will be sent
            for each completed interview.
          </div>
        </div>
      );
    };

    return (
      <>
        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.JOB_OPENINGS}
          passThrough={true}
        >
          {(allowed) => (
            <Checkbox
              isChecked={isSlackEnabled}
              onChange={setIsSlackEnabled}
              label="Send Slack notifications when interviews are completed"
              isDisabled={!allowed}
            />
          )}
        </Can>
        {maybeRenderSelect()}
        {hasChanges ? (
          <SaveAndCancelButtons
            onSave={() => {
              onSave({
                id: project.id,
                settings: {
                  ...project.settings,
                  is_slack_enabled: isSlackEnabled,
                  slack_channel_id: slackChannelId,
                },
              });
            }}
            onCancel={() => {
              setIsSlackEnabled(project.settings.is_slack_enabled);
              setSlackChannelId(project.settings.slack_channel_id);
            }}
          />
        ) : null}
      </>
    );
  };

  return (
    <div className="mb-3 flex flex-col justify-between">
      <Label size={Size.SMALL} className="mb-2">
        Slack
      </Label>
      {renderContent()}
    </div>
  );
};
